// MorningHome.jsx — POR-driven home screen
// Compares Plan of Record targets to live actuals and surfaces shortfalls with navigation CTAs.
import React, { useMemo } from "react";
import { ChevronRight, AlertTriangle, CheckCircle, TrendingDown, TrendingUp, ArrowRight, Settings } from "lucide-react";
import { fmtM, resolveF } from "../shared/constants";

// ── helpers ──────────────────────────────────────────────────────────────────
const pct     = (v, d = 0) => v == null ? "—" : `${(v * 100).toFixed(d)}%`;
const clr     = (ratio, warn = 0.85, good = 1.0) => ratio >= good ? "#16A34A" : ratio >= warn ? "#D97706" : "#DC2626";
const bg      = (ratio, warn = 0.85, good = 1.0) => ratio >= good ? "rgba(22,163,74,0.08)" : ratio >= warn ? "rgba(217,119,6,0.08)" : "rgba(220,38,38,0.07)";
const parseDollar = (raw) => {
  const s = String(raw || "").replace(/[^0-9.KkMm]/g, "");
  if (!s) return 0;
  if (/[Mm]/.test(s)) return parseFloat(s) * 1_000_000;
  if (/[Kk]/.test(s)) return parseFloat(s) * 1_000;
  return parseFloat(s) || 0;
};
const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const SEG_COLORS = ["#CC1A1A","#1A6BCC","#16A34A","#D97706","#7C3AED","#0891B2","#DB2777","#059669"];

// ── sub-components ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, sub, action, onAction }) => (
  <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:12 }}>
    <div>
      <span style={{ fontSize:11, fontWeight:800, color:"#111", textTransform:"uppercase", letterSpacing:"0.08em" }}>{title}</span>
      {sub && <span style={{ fontSize:11, color:"#999", marginLeft:8 }}>{sub}</span>}
    </div>
    {action && (
      <button onClick={onAction} style={{ display:"flex", alignItems:"center", gap:3, fontSize:11, color:"#CC1A1A", fontWeight:700, background:"none", border:"none", cursor:"pointer", padding:0 }}>
        {action} <ChevronRight size={12} />
      </button>
    )}
  </div>
);

// Gap card — shows a POR target vs actual with navigation
const GapCard = ({ label, plan, actual, planLabel, actualLabel, unit = "", suffix = "", ratio, nav, navLabel, onNavigate, color, icon }) => {
  const r = ratio ?? (plan > 0 ? actual / plan : null);
  const c = r != null ? clr(r) : "#888";
  const b = r != null ? bg(r) : "#F8F8F8";
  const delta = plan > 0 ? actual - plan : null;
  const deltaFmt = unit === "$" ? fmtM(Math.abs(delta || 0)) : Math.abs(Math.round((delta || 0) * (suffix === "%" ? 100 : 1))) + suffix;
  const isGood = r != null && r >= 1.0;
  return (
    <div onClick={() => onNavigate(nav)}
      style={{ background:"#FFF", borderRadius:12, border:`1px solid ${r != null && r < 0.85 ? c + "33" : "rgba(0,0,0,0.07)"}`,
        padding:"16px 18px", cursor:"pointer", display:"flex", flexDirection:"column", gap:10, transition:"box-shadow 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 3px 16px rgba(0,0,0,0.10)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div style={{ fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</div>
        {icon && <span style={{ fontSize:16 }}>{icon}</span>}
      </div>
      <div style={{ display:"flex", gap:16, alignItems:"flex-end" }}>
        <div>
          <div style={{ fontSize:10, color:"#AAA", marginBottom:2 }}>Actual</div>
          <div style={{ fontSize:22, fontWeight:800, color:c, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>
            {unit}{actualLabel}
          </div>
        </div>
        <div style={{ paddingBottom:3 }}>
          <div style={{ fontSize:10, color:"#AAA", marginBottom:2 }}>Plan</div>
          <div style={{ fontSize:14, fontWeight:700, color:"#888", fontFamily:"'DM Mono',monospace" }}>
            {unit}{planLabel}
          </div>
        </div>
      </div>
      {delta != null && (
        <div style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 8px", borderRadius:6, background:b, width:"fit-content" }}>
          {isGood ? <TrendingUp size={11} color={c} /> : <TrendingDown size={11} color={c} />}
          <span style={{ fontSize:11, fontWeight:700, color:c }}>
            {isGood ? `+${deltaFmt} ahead of plan` : `${deltaFmt} below plan`}
          </span>
        </div>
      )}
      <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:"auto", paddingTop:4 }}>
        <span style={{ fontSize:11, color:"#CC1A1A", fontWeight:600 }}>{navLabel}</span>
        <ArrowRight size={11} color="#CC1A1A" />
      </div>
    </div>
  );
};

// Setup step indicator
const SetupStep = ({ done, label, nav, onNavigate }) => (
  <div onClick={() => !done && onNavigate(nav)}
    style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", borderRadius:8,
      background: done ? "rgba(22,163,74,0.05)" : "rgba(220,38,38,0.04)",
      border: `1px solid ${done ? "rgba(22,163,74,0.15)" : "rgba(220,38,38,0.15)"}`,
      cursor: done ? "default" : "pointer" }}>
    {done
      ? <CheckCircle size={14} color="#16A34A" />
      : <AlertTriangle size={14} color="#DC2626" />}
    <span style={{ fontSize:12, fontWeight:600, color: done ? "#16A34A" : "#DC2626", flex:1 }}>{label}</span>
    {!done && <ChevronRight size={12} color="#DC2626" />}
  </div>
);

// ── main component ────────────────────────────────────────────────────────────

// ── Team Quota Coverage Card ────────────────────────────────────────────────
// Shows per-team quota coverage vs their proportional share of the revenue target.
// Flags single-rep teams (key-person risk) and teams below 1.0x coverage.
const TeamCoverageCard = ({ teamStats, totalQuota, annualTarget, onNavigate }) => {
  const overallCoverage = annualTarget > 0 ? totalQuota / annualTarget : 0;
  const coverageColor   = overallCoverage >= 1.1 ? "#16A34A" : overallCoverage >= 1.0 ? "#D97706" : "#DC2626";

  return (
    <div onClick={() => onNavigate("planbuilder")}
      style={{ background:"#FFF", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)",
        padding:"16px 18px", cursor:"pointer", display:"flex", flexDirection:"column", gap:10,
        transition:"box-shadow 0.15s", minWidth:0 }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 3px 16px rgba(0,0,0,0.10)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div style={{ fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:"0.07em" }}>
          Quota Coverage
        </div>
        <span style={{ fontSize:16 }}>👥</span>
      </div>

      {/* Overall ratio */}
      <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
        <span style={{ fontSize:22, fontWeight:900, color:coverageColor, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>
          {overallCoverage.toFixed(2)}x
        </span>
        <span style={{ fontSize:11, color:"#888" }}>
          {fmtM(totalQuota)} quota vs {fmtM(annualTarget)} target
        </span>
      </div>

      {/* Per-team rows */}
      <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
        {teamStats.map((ts, i) => {
          // Each team's proportional share of target = their % of total quota × annual target
          const teamShare    = totalQuota > 0 ? (ts.teamQuota / totalQuota) * annualTarget : 0;
          const teamCoverage = teamShare > 0 ? ts.teamQuota / teamShare : 0;
          const repCount     = ts.teamReps.length;
          const singleRepRisk = repCount === 1;
          const c = teamCoverage >= 1.1 ? "#16A34A" : teamCoverage >= 0.95 ? "#D97706" : "#DC2626";
          const barW = Math.min(100, (ts.teamQuota / (annualTarget || 1)) * 100);

          return (
            <div key={ts.team} style={{ display:"flex", alignItems:"center", gap:8 }}>
              {/* Color dot */}
              <div style={{ width:7, height:7, borderRadius:"50%", background:ts.color, flexShrink:0 }} />

              {/* Team name + rep count */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                  <span style={{ fontSize:10, fontWeight:600, color:"#333", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                    {ts.team}
                    {singleRepRisk && (
                      <span title="Single rep — key-person risk if they leave"
                        style={{ marginLeft:5, fontSize:9, color:"#D97706", fontWeight:700 }}>⚠ 1 rep</span>
                    )}
                  </span>
                  <span style={{ fontSize:10, fontWeight:700, color:c, fontFamily:"'DM Mono',monospace", flexShrink:0, marginLeft:6 }}>
                    {fmtM(ts.teamQuota)}
                  </span>
                </div>
                {/* Coverage bar */}
                <div style={{ height:3, background:"rgba(0,0,0,0.06)", borderRadius:2, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${barW}%`, background:c, borderRadius:2, transition:"width 0.4s" }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:"auto", paddingTop:4 }}>
        <span style={{ fontSize:11, color:"#CC1A1A", fontWeight:600 }}>Adjust Team</span>
        <ArrowRight size={11} color="#CC1A1A" />
      </div>
    </div>
  );
};

const MorningHome = ({ settings, hsData, onNavigate }) => {
  const rawDeals  = hsData?.rawDeals || [];
  const wonDeals  = hsData?.rawWonDeals || [];
  const metrics   = hsData?.metrics;
  const F         = resolveF(settings);

  const now = new Date();
  const companyName = settings?.company?.name || "Your Company";

  // ── POR targets ────────────────────────────────────────────────────────────
  const annualTarget  = parseDollar(settings?.revenue?.target || 0);
  const planWR        = +(settings?.targets?.winRate   || 0) / 100;
  const planACV       = parseDollar(settings?.targets?.avgDealSize || 0);
  const planCoverage  = +(settings?.targets?.coverageRatio || 3);
  const reps          = (settings?.team?.reps || []).filter(r => r.name);
  const segments      = settings?.segments?.list || [];
  const byTeam        = settings?.targets?.byTeam || {};
  const monthlyTargets = settings?.revenue?.monthlyTargets || {};

  // Total quota pool
  const totalQuota = reps.reduce((s, r) => s + parseDollar(r.quota || ""), 0);
  const quotaCoverage = annualTarget > 0 ? totalQuota / annualTarget : 0;

  // ── POR completeness ───────────────────────────────────────────────────────
  const porSteps = [
    { done: annualTarget > 0,              label: "Revenue target set",         nav: "planbuilder" },
    { done: reps.length > 0,               label: "Team & quotas configured",   nav: "planbuilder" },
    { done: segments.length > 0,           label: "Revenue matrix defined",     nav: "planbuilder" },
    { done: planWR > 0 || planACV > 0,     label: "Performance targets set",    nav: "planbuilder" },
    { done: !!settings?.hubspot?.configured, label: "HubSpot connected",        nav: "planbuilder"    },
  ];
  const porComplete = porSteps.filter(s => s.done).length;
  const porPct      = porComplete / porSteps.length;

  // ── Deal calcs (memoized) ──────────────────────────────────────────────────
  const dealData = useMemo(() => {
    const mappings = settings?.stages?.mappings || [];
    const wonIds   = mappings.filter(m => m.type === "won").map(m => m.id.toLowerCase());
    const lostIds  = mappings.filter(m => m.type === "lost").map(m => m.id.toLowerCase());
    const isWon  = d => { const s = (d.properties?.[F.dealstage] || "").toLowerCase(); return wonIds.length ? wonIds.includes(s) : s.includes("closedwon"); };
    const isLost = d => { const s = (d.properties?.[F.dealstage] || "").toLowerCase(); return lostIds.length ? lostIds.includes(s) : s.includes("closedlost"); };
    const isOpen = d => !isWon(d) && !isLost(d);
    const amt    = d => +(d.properties?.[F.amount] || 0);
    const closeD = d => d.properties?.[F.closedate];
    const ownId  = d => String(d.properties?.[F.owner] || d.properties?.hubspot_owner_id || "");

    const fyStart  = new Date(now.getFullYear(), 0, 1).getTime();
    const fyEnd    = new Date(now.getFullYear(), 11, 31, 23, 59, 59).getTime();
    const l12mStart = now.getTime() - 365 * 86400000; // rolling 12 months

    const dedupMap = new Map();
    [...rawDeals, ...wonDeals].forEach(d => { if (!dedupMap.has(d.id)) dedupMap.set(d.id, d); });
    const allDeals  = Array.from(dedupMap.values());
    const allWon    = allDeals.filter(isWon);
    const allLost   = allDeals.filter(isLost);
    const openDeals = rawDeals.filter(isOpen);
    const fyWon     = allWon.filter(d => closeD(d) && new Date(closeD(d)).getTime() >= fyStart && new Date(closeD(d)).getTime() <= fyEnd);
    const l12Won    = allWon.filter( d => closeD(d) && new Date(closeD(d)).getTime() >= l12mStart);
    const l12Lost   = allLost.filter(d => closeD(d) && new Date(closeD(d)).getTime() >= l12mStart);

    // Week window
    const wk = new Date(now); wk.setDate(now.getDate() - now.getDay()); wk.setHours(0,0,0,0);
    const wkEnd = new Date(wk); wkEnd.setDate(wk.getDate() + 6); wkEnd.setHours(23,59,59,999);
    const closing = openDeals.filter(d => closeD(d) && new Date(closeD(d)).getTime() >= wk.getTime() && new Date(closeD(d)).getTime() <= wkEnd.getTime())
      .sort((a, b) => amt(b) - amt(a));

    // Risk
    const pastClose = openDeals.filter(d => closeD(d) && new Date(closeD(d)).getTime() < now.getTime()).sort((a, b) => amt(b) - amt(a));
    const stale     = openDeals.filter(d => {
      const lastAct = d.properties?.[F.lastActivity] || d.properties?.notes_last_activity_date;
      const lastMod = d.properties?.[F.lastModified] || d.properties?.hs_lastmodifieddate;
      const ts = lastAct ? new Date(lastAct).getTime() : lastMod ? new Date(lastMod).getTime() : 0;
      return ts > 0 && (now.getTime() - ts) > 30 * 86400000;
    }).sort((a, b) => amt(b) - amt(a));

    return { isWon, isLost, isOpen, amt, closeD, ownId, allWon, allLost, openDeals, fyWon, l12Won, l12Lost, closing, pastClose, stale, wk, wkEnd };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDeals, wonDeals, settings?.stages?.mappings, F.dealstage]);

  const { amt, closeD, ownId, allWon, allLost, openDeals, fyWon, l12Won, l12Lost, closing, pastClose, stale, wk, wkEnd } = dealData;

  const hasData = rawDeals.length > 0 || wonDeals.length > 0;

  // ── Revenue pace ───────────────────────────────────────────────────────────
  const ytdBooks = fyWon.reduce((a, d) => a + amt(d), 0);
  const pipeline = openDeals.reduce((a, d) => a + amt(d), 0);
  const coverage = annualTarget > 0 ? pipeline / annualTarget : 0;
  // Win rate: rolling 12 months for stability (always a full sample, even in Jan)
  const l12WRDenom = l12Won.length + l12Lost.length;
  const liveWR     = l12WRDenom > 0 ? l12Won.length / l12WRDenom : (metrics?.winRate || 0);
  const liveACV  = metrics?.avgDealSize || 0;
  const forecast = pipeline * liveWR;

  const expectedYTD = (() => {
    const cm = now.getMonth();
    let total = 0;
    for (let m = 0; m < cm; m++) {
      total += +monthlyTargets[MONTH_KEYS[m]] || annualTarget / 12;
    }
    const monthTgt = +monthlyTargets[MONTH_KEYS[cm]] || annualTarget / 12;
    const daysInMonth = new Date(now.getFullYear(), cm + 1, 0).getDate();
    total += monthTgt * (now.getDate() / daysInMonth);
    return total;
  })();

  const ytdAtt   = annualTarget > 0 && hasData ? ytdBooks / annualTarget : null;
  const paceAtt  = annualTarget > 0 ? expectedYTD / annualTarget : null;
  const onPace   = ytdAtt != null && paceAtt != null ? ytdAtt >= paceAtt * 0.9 : null;
  const paceGap  = expectedYTD > 0 ? ytdBooks - expectedYTD : null;

  // ── Per-team stats ─────────────────────────────────────────────────────────
  const teamNames = [...new Set(reps.map(r => r.team).filter(Boolean))];
  const teamStats = teamNames.map((team, ti) => {
    const teamReps  = reps.filter(r => r.team === team);
    const teamQuota = teamReps.reduce((s, r) => s + parseDollar(r.quota || ""), 0);
    const targets   = byTeam[team] || {};
    const color     = SEG_COLORS[ti % SEG_COLORS.length];

    // Actuals for this team
    const teamOwnerIds = teamReps.map(r => String(r.hubspotOwnerId)).filter(Boolean);
    const teamWon  = fyWon.filter(d => teamOwnerIds.includes(ownId(d)));
    const teamLost = dealData.allLost.filter(d => teamOwnerIds.includes(ownId(d)));
    const teamOpen = openDeals.filter(d => teamOwnerIds.includes(ownId(d)));
    const teamBooks = teamWon.reduce((a, d) => a + amt(d), 0);
    const teamPipe  = teamOpen.reduce((a, d) => a + amt(d), 0);
    const totCl     = teamWon.length + teamLost.length;
    const teamWR    = totCl > 0 ? teamWon.length / totCl : null;
    const teamACV   = teamWon.length > 0 ? teamBooks / teamWon.length : null;
    const attainment = teamQuota > 0 ? teamBooks / teamQuota : null;

    return { team, teamReps, teamQuota, targets, color, teamBooks, teamPipe, teamWR, teamACV, attainment };
  });

  // ── Gap to Goal diagnosis ──────────────────────────────────────────────────
  // Must be declared BEFORE action queue (which references gapDiagnosis)
  const gapDiagnosis = useMemo(() => {
    if (!hasData || !annualTarget || onPace !== false) return null;

    const gap = Math.abs(paceGap || 0);
    const flags = [];

    const pipeRatio = planCoverage > 0 ? coverage / planCoverage : null;
    if (pipeRatio != null && pipeRatio < 0.75) {
      const missingPipe = annualTarget * planCoverage - pipeline;
      flags.push({
        type: "pipeline",
        severity: pipeRatio < 0.5 ? "critical" : "high",
        label: "Lead / Pipeline Shortage",
        owner: "Marketing + BDR",
        detail: `Pipeline is ${coverage.toFixed(1)}x vs ${planCoverage}x target. Need ${fmtM(missingPipe)} more pipe to cover the number.`,
        action: "Build pipeline now",
        nav: "coverage",
        icon: "🪣",
      });
    }

    const wrRatio = planWR > 0 && liveWR > 0 ? liveWR / planWR : null;
    if (wrRatio != null && wrRatio < 0.85) {
      const wrGapPp = Math.round((planWR - liveWR) * 100);
      flags.push({
        type: "winrate",
        severity: wrRatio < 0.65 ? "critical" : "high",
        label: "Win Rate Underperformance",
        owner: "Sales / Rep Coaching",
        detail: `Closing ${pct(liveWR)} vs ${pct(planWR)} target — ${wrGapPp}pp gap is costing ${fmtM(pipeline * (planWR - liveWR))} in forecasted revenue.`,
        action: "Review reps",
        nav: "coaching",
        icon: "🎯",
      });
    }

    const acvRatio = planACV > 0 && liveACV > 0 ? liveACV / planACV : null;
    if (acvRatio != null && acvRatio < 0.85) {
      flags.push({
        type: "dealsize",
        severity: acvRatio < 0.7 ? "high" : "med",
        label: "Deal Size Below Plan",
        owner: "Sales + Marketing",
        detail: `Avg deal ${fmtM(liveACV)} vs ${fmtM(planACV)} plan — winning smaller deals than modeled. Check ICP targeting and qualification.`,
        action: "Check deal mix",
        nav: "coaching",
        icon: "💰",
      });
    }

    if (flags.length === 0 && gap > 0) {
      flags.push({
        type: "pace",
        severity: "med",
        label: "Booking Pace Behind Schedule",
        owner: "Sales Leadership",
        detail: `${fmtM(gap)} behind YTD pace. Metrics look healthy — this is a volume/timing issue, not a structural one.`,
        action: "Check monthly targets",
        nav: "por",
        icon: "📉",
      });
    }

    const order = { critical: 0, high: 1, med: 2 };
    flags.sort((a, b) => order[a.severity] - order[b.severity]);
    return flags;
  }, [hasData, annualTarget, onPace, paceGap, coverage, planCoverage, liveWR, planWR, liveACV, planACV, pipeline]);

  // ── Quiet rep detection ────────────────────────────────────────────────────
  // Must be declared BEFORE action queue (which references quietReps)
  const quietReps = useMemo(() => {
    if (!hasData) return [];
    return reps.filter(rep => {
      const quota = parseDollar(rep.quota || "");
      if (!quota || !rep.hubspotOwnerId) return false;
      const repOwner = String(rep.hubspotOwnerId);
      const repDeals = openDeals.filter(d => ownId(d) === repOwner);
      if (repDeals.length === 0) return false;
      const lastTouch = repDeals.reduce((latest, d) => {
        const lastAct = d.properties?.[F.lastActivity] || d.properties?.notes_last_activity_date;
        const lastMod = d.properties?.[F.lastModified] || d.properties?.hs_lastmodifieddate;
        const ts = lastAct ? new Date(lastAct).getTime() : lastMod ? new Date(lastMod).getTime() : 0;
        return ts > latest ? ts : latest;
      }, 0);
      return lastTouch > 0 && (now.getTime() - lastTouch) > 30 * 86400000;
    });
  }, [hasData, reps, openDeals, F]);

  // ── Action queue — derive most important things to fix ────────────────────
  const actions = [];

  if (!settings?.hubspot?.configured) {
    actions.push({ icon: "🔌", text: "Connect HubSpot to pull live actuals — without it the POR is just a guess", nav: "planbuilder", cta: "Connect Now", urgency: "high" });
  }
  if (porPct < 1 && porComplete < 4) {
    const missing = porSteps.filter(s => !s.done).map(s => s.label).slice(0, 2).join(", ");
    actions.push({ icon: "📋", text: `Finish POR setup to unlock all insights — missing: ${missing}`, nav: "planbuilder", cta: "Finish Setup", urgency: "high" });
  }
  if (hasData && onPace === false && paceGap != null) {
    const primary = gapDiagnosis?.[0];
    const reason = primary ? ` Root cause: ${primary.label}.` : "";
    actions.push({ icon: "📉", text: `Revenue ${fmtM(Math.abs(paceGap))} behind pace.${reason} Adjust the plan or accelerate pipeline now.`, nav: primary?.nav || "dashboard", cta: primary ? primary.action : "View Dashboard", urgency: "high" });
  }
  if (hasData && planWR > 0 && liveWR > 0 && liveWR < planWR * 0.85) {
    const wrGapPp = Math.round((planWR - liveWR) * 100);
    actions.push({ icon: "🎯", text: `Win rate ${pct(liveWR)} — ${wrGapPp}pp below plan. Pull the bottom 3 reps for a deal review this week.`, nav: "coaching", cta: "Review Reps", urgency: "high" });
  }
  if (hasData && coverage < planCoverage * 0.75 && annualTarget > 0) {
    const missing = fmtM(annualTarget * planCoverage - pipeline);
    actions.push({ icon: "🪣", text: `Pipeline ${coverage.toFixed(1)}x — need ${missing} more to reach ${planCoverage}x. Request a Marketing campaign or push BDR outreach.`, nav: "coverage", cta: "Coverage Map", urgency: "high" });
  }
  if (quietReps.length > 0) {
    const names = quietReps.slice(0, 2).map(r => r.name).join(", ");
    const more  = quietReps.length > 2 ? ` +${quietReps.length - 2} more` : "";
    actions.push({ icon: "💤", text: `Quiet reps: ${names}${more} — high quota, zero deal activity in 30+ days. Schedule 1-on-1 pipeline reviews.`, nav: "coaching", cta: "Coaching", urgency: "high" });
  }
  if (quotaCoverage > 0 && quotaCoverage < 1.0 && annualTarget > 0) {
    actions.push({ icon: "👥", text: `Quota pool ${fmtM(totalQuota)} is ${fmtM(annualTarget - totalQuota)} short of target. Add reps or increase individual quotas before Q2.`, nav: "planbuilder", cta: "Adjust Team", urgency: "med" });
  }
  if (pastClose.length > 3) {
    const val = fmtM(pastClose.reduce((a, d) => a + amt(d), 0));
    const q   = now.getMonth() < 9 ? `Q${Math.ceil((now.getMonth() + 2) / 3)}` : "next year";
    actions.push({ icon: "📅", text: `${pastClose.length} deals past close date (${val} at risk) — push reps for updates or slip to ${q} today.`, nav: "crm", cta: "CRM Health", urgency: "med" });
  }
  if (stale.length > 2) {
    actions.push({ icon: "💤", text: `${stale.length} deals untouched for 30+ days — review each in coaching and decide: push or purge.`, nav: "crm", cta: "CRM Health", urgency: "low" });
  }
  const topActions = actions.slice(0, 5);

  // ── Forecast breakdown ──────────────────────────────────────────────────────
  const forecastData = useMemo(() => {
    if (!openDeals.length) return null;
    const commit    = openDeals.filter(d => (d.properties?.hs_forecast_category||"").toLowerCase() === "commit");
    const bestCase  = openDeals.filter(d => (d.properties?.hs_forecast_category||"").toLowerCase() === "best_case");
    const pipeline  = openDeals.filter(d => {
      const fc = (d.properties?.hs_forecast_category||"").toLowerCase();
      return fc === "pipeline" || fc === "";
    });
    const sum = arr => arr.reduce((a,d) => a + amt(d), 0);
    const commitVal   = sum(commit);
    const bestVal     = sum(bestCase);
    const pipeVal     = sum(pipeline);
    const weightedForecast = commitVal + bestVal * 0.5 + pipeVal * liveWR;
    return { commit, bestCase, pipeline, commitVal, bestVal, pipeVal, weightedForecast };
  }, [openDeals, liveWR]);

  // ── Rep scorecard ────────────────────────────────────────────────────────
  const repScorecard = useMemo(() => {
    if (!hasData || !reps.length) return [];
    return reps.filter(r => r.hubspotOwnerId).map(rep => {
      const oid      = String(rep.hubspotOwnerId);
      const quota    = parseDollar(rep.quota || "");
      const repWon   = fyWon.filter(d => String(d.properties?.[F.owner]||d.properties?.hubspot_owner_id||"") === oid);
      const repOpen  = openDeals.filter(d => String(d.properties?.[F.owner]||d.properties?.hubspot_owner_id||"") === oid);
      const repLost  = dealData.allLost.filter(d => String(d.properties?.[F.owner]||d.properties?.hubspot_owner_id||"") === oid);
      const booked   = repWon.reduce((a,d) => a+amt(d), 0);
      const pipe     = repOpen.reduce((a,d) => a+amt(d), 0);
      const att      = quota > 0 ? booked / quota : null;
      const cov      = quota > 0 && planCoverage > 0 ? (pipe/quota)/planCoverage : null;
      const totCl    = repWon.length + repLost.length;
      const wr       = totCl > 0 ? repWon.length / totCl : null;
      // RAG status
      const status   = att == null ? "gray"
        : att >= 0.9 ? "green"
        : att >= 0.6 ? "yellow"
        : "red";
      return { rep, quota, booked, pipe, att, cov, wr, status, openCount: repOpen.length };
    }).sort((a,b) => (b.att||0) - (a.att||0));
  }, [hasData, reps, fyWon, openDeals, dealData.allLost, F]);

  // ── Month-over-month pace comparison ─────────────────────────────────────
  const momData = useMemo(() => {
    if (!hasData || !annualTarget) return null;
    const cm = now.getMonth();
    const cy = now.getFullYear();
    // This month won deals
    const thisMonthWon  = fyWon.filter(d => {
      const dt = closeD(d) ? new Date(closeD(d)) : null;
      return dt && dt.getMonth() === cm && dt.getFullYear() === cy;
    });
    // Last month won deals
    const lm = cm === 0 ? 11 : cm - 1;
    const ly = cm === 0 ? cy - 1 : cy;
    const lastMonthWon  = [...fyWon, ...dealData.allWon].filter(d => {
      const dt = closeD(d) ? new Date(closeD(d)) : null;
      return dt && dt.getMonth() === lm && dt.getFullYear() === ly;
    });
    const thisMonthVal  = thisMonthWon.reduce((a,d) => a+amt(d), 0);
    const lastMonthVal  = lastMonthWon.reduce((a,d) => a+amt(d), 0);
    const thisMonthTgt  = +monthlyTargets[MONTH_KEYS[cm]] || annualTarget/12;
    const lastMonthTgt  = +monthlyTargets[MONTH_KEYS[lm]] || annualTarget/12;
    return { thisMonthVal, lastMonthVal, thisMonthTgt, lastMonthTgt,
      thisAtt: thisMonthTgt > 0 ? thisMonthVal/thisMonthTgt : null,
      lastAtt: lastMonthTgt > 0 ? lastMonthVal/lastMonthTgt : null,
      mom: lastMonthVal > 0 ? (thisMonthVal - lastMonthVal)/lastMonthVal : null,
    };
  }, [hasData, fyWon, dealData.allWon, annualTarget, monthlyTargets, now]);

  // ── Pipeline concentration risks ──────────────────────────────────────────
  const pipelineRisks = useMemo(() => {
    if (!hasData || !openDeals.length) return [];
    const risks = [];
    const totalPipe = openDeals.reduce((a,d) => a+amt(d), 0);
    if (totalPipe === 0) return [];

    // 1. Single-rep concentration — one rep owns >40% of pipeline value
    const byRep = {};
    openDeals.forEach(d => {
      const id = ownId(d);
      if (!byRep[id]) byRep[id] = { val:0, count:0 };
      byRep[id].val += amt(d); byRep[id].count++;
    });
    const topRep = Object.entries(byRep).sort((a,b)=>b[1].val-a[1].val)[0];
    if (topRep) {
      const repShare = topRep[1].val / totalPipe;
      if (repShare > 0.4) {
        const repName = reps.find(r => String(r.hubspotOwnerId) === topRep[0])?.name || "One rep";
        risks.push({
          icon:"👤", color:"#DC2626",
          label:"Rep Concentration Risk",
          detail:`${repName} owns ${pct(repShare)} of pipeline (${fmtM(topRep[1].val)}). If they slip, the number slips.`,
          severity:"high",
        });
      }
    }

    // 2. Single-company concentration — one company >20% of pipeline
    const byCompany = {};
    openDeals.forEach(d => {
      const co = d.properties?.company || d.properties?.hs_object_id || "Unknown";
      if (!byCompany[co]) byCompany[co] = { val:0, count:0, name:co };
      byCompany[co].val += amt(d); byCompany[co].count++;
    });
    const topCo = Object.values(byCompany).sort((a,b)=>b.val-a.val)[0];
    if (topCo && topCo.val / totalPipe > 0.2 && topCo.name !== "Unknown") {
      risks.push({
        icon:"🏢", color:"#D97706",
        label:"Account Concentration Risk",
        detail:`${topCo.name} represents ${pct(topCo.val/totalPipe)} of pipeline (${fmtM(topCo.val)}). A single loss would materially impact the forecast.`,
        severity:"high",
      });
    }

    // 3. High-value dark deals — deals >median ACV with no activity in 14 days
    const medianACV = liveACV || planACV || 0;
    const cutoff14  = now.getTime() - 14 * 86400000;
    const darkHighValue = openDeals.filter(d => {
      if (amt(d) < medianACV * 0.8) return false;
      const lastAct = d.properties?.notes_last_activity_date || d.properties?.hs_lastmodifieddate;
      if (!lastAct) return true;
      return new Date(lastAct).getTime() < cutoff14;
    });
    if (darkHighValue.length > 0) {
      risks.push({
        icon:"🔕", color:"#7C3AED",
        label:"High-Value Deals Gone Dark",
        detail:`${darkHighValue.length} deal${darkHighValue.length!==1?"s":""} above median ACV with no activity in 14+ days — ${fmtM(darkHighValue.reduce((a,d)=>a+amt(d),0))} at risk.`,
        severity:"high",
      });
    }

    // 4. End-of-quarter hockey stick — >35% of pipeline closing in last 2 weeks of quarter
    const qEndMonth = [2,5,8,11][Math.floor(now.getMonth()/3)];
    const qEndYear  = now.getFullYear() + (qEndMonth < now.getMonth() ? 1 : 0);
    const qEnd      = new Date(qEndYear, qEndMonth+1, 0).getTime();
    const twoWksBefore = qEnd - 14 * 86400000;
    const lastMinute = openDeals.filter(d => {
      const cd = closeD(d) ? new Date(closeD(d)).getTime() : 0;
      return cd > twoWksBefore && cd <= qEnd;
    });
    const lastMinuteVal = lastMinute.reduce((a,d)=>a+amt(d),0);
    if (lastMinuteVal / totalPipe > 0.35) {
      risks.push({
        icon:"📅", color:"#D97706",
        label:"End-of-Quarter Hockey Stick",
        detail:`${pct(lastMinuteVal/totalPipe)} of pipeline (${fmtM(lastMinuteVal)}) closes in the last 2 weeks of the quarter. Forecast reliability is low.`,
        severity:"med",
      });
    }

    // 5. Single-threaded high-value deals — >median ACV, only 1 contact
    const singleThreaded = openDeals.filter(d =>
      amt(d) >= medianACV * 0.8 &&
      +(d.properties?.num_associated_contacts||0) <= 1
    );
    if (singleThreaded.length > 0) {
      risks.push({
        icon:"🧵", color:"#0891B2",
        label:"Single-Threaded Deals",
        detail:`${singleThreaded.length} high-value deal${singleThreaded.length!==1?"s":""} with only 1 contact — ${fmtM(singleThreaded.reduce((a,d)=>a+amt(d),0))}. No champion = high risk.`,
        severity:"med",
      });
    }

    // 6. Past close date (keep existing)
    if (pastClose.length > 0) {
      risks.push({
        icon:"📅", color:"#D97706",
        label:"Past Close Date",
        detail:`${pastClose.length} deal${pastClose.length!==1?"s":""} with close date already passed — ${fmtM(pastClose.reduce((a,d)=>a+amt(d),0))} needs date updates or to be marked lost.`,
        severity: pastClose.length > 5 ? "high" : "med",
      });
    }

    // 7. Stale deals — no activity 30+ days (keep existing, better label)
    if (stale.length > 0) {
      risks.push({
        icon:"💤", color:"#555",
        label:`No Activity 30+ Days`,
        detail:`${stale.length} deal${stale.length!==1?"s":""} untouched for 30+ days — ${fmtM(stale.reduce((a,d)=>a+amt(d),0))}. Decide: push or remove from pipeline.`,
        severity:"low",
      });
    }

    return risks.sort((a,b) => ({high:0,med:1,low:2}[a.severity]-({high:0,med:1,low:2}[b.severity])));
  }, [hasData, openDeals, reps, liveACV, planACV, pastClose, stale, now]);

  const fmtDate  = d => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric" }) : "—";
  const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 17 ? "Good afternoon" : "Good evening";

  return (
    <div style={{ maxWidth: 1140, margin: "0 auto", paddingBottom: 60 }}>

      {/* ── HERO HEADER ──────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 28, padding: "24px 28px", background: "#111", borderRadius: 16, position: "relative", overflow: "hidden" }}>
        {/* subtle grid texture */}
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(circle at 80% 50%, rgba(204,26,26,0.15) 0%, transparent 60%)", pointerEvents:"none" }} />
        <div style={{ position:"relative" }}>
          <div style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.4)", marginBottom:6, letterSpacing:"0.06em", textTransform:"uppercase" }}>
            {now.toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric", year:"numeric" })}
          </div>
          <div style={{ fontSize:22, fontWeight:800, color:"#FFF", marginBottom:16, letterSpacing:"-0.01em" }}>
            {greeting}, {companyName}
          </div>

          {/* Big pace indicator */}
          {annualTarget > 0 ? (
            <div style={{ display:"flex", alignItems:"center", gap:24, flexWrap:"wrap" }}>
              <div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>Revenue Target</div>
                <div style={{ fontSize:28, fontWeight:900, color:"#FFF", fontFamily:"'DM Mono',monospace", letterSpacing:"-0.02em" }}>{fmtM(annualTarget)}</div>
              </div>
              {hasData && ytdAtt != null && (
                <>
                  <div style={{ width:1, height:44, background:"rgba(255,255,255,0.1)" }} />
                  <div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>Booked YTD</div>
                    <div style={{ fontSize:28, fontWeight:900, color: onPace ? "#4ADE80" : "#F87171", fontFamily:"'DM Mono',monospace", letterSpacing:"-0.02em" }}>
                      {fmtM(ytdBooks)}
                    </div>
                  </div>
                  <div style={{ width:1, height:44, background:"rgba(255,255,255,0.1)" }} />
                  <div style={{ flex:1, minWidth:200 }}>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>
                      Pace · {pct(ytdAtt)} vs {pct(paceAtt)} expected
                    </div>
                    <div style={{ height:6, background:"rgba(255,255,255,0.1)", borderRadius:99, position:"relative", overflow:"visible" }}>
                      <div style={{ height:"100%", borderRadius:99, background: onPace ? "#4ADE80" : "#F87171", width:`${Math.min(100, (ytdAtt || 0) * 100)}%`, transition:"width 0.6s" }} />
                      {paceAtt != null && (
                        <div style={{ position:"absolute", top:-4, height:14, width:2, background:"rgba(255,255,255,0.5)", borderRadius:1, left:`${Math.min(99, paceAtt * 100)}%` }} />
                      )}
                    </div>
                    <div style={{ fontSize:11, fontWeight:700, color: onPace ? "#4ADE80" : "#F87171", marginTop:6 }}>
                      {onPace ? "✅ On pace" : `⚠️ ${fmtM(Math.abs(paceGap || 0))} behind pace`}
                      {" · "}
                      <span style={{ color:"rgba(255,255,255,0.5)", fontWeight:400 }}>{fmtM(pipeline)} in pipeline</span>
                    </div>
                  </div>
                </>
              )}
              {!hasData && (
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", background:"rgba(255,255,255,0.07)", borderRadius:10, cursor:"pointer" }}
                  onClick={() => onNavigate("planbuilder")}>
                  <span style={{ fontSize:12, color:"rgba(255,255,255,0.6)" }}>Connect HubSpot to see live actuals</span>
                  <ArrowRight size={14} color="rgba(255,255,255,0.4)" />
                </div>
              )}
            </div>
          ) : (
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", background:"rgba(204,26,26,0.15)", border:"1px solid rgba(204,26,26,0.3)", borderRadius:10, cursor:"pointer" }}
              onClick={() => onNavigate("planbuilder")}>
              <AlertTriangle size={16} color="#F87171" />
              <span style={{ fontSize:12, color:"rgba(255,255,255,0.8)", fontWeight:600 }}>No revenue target set — complete your POR setup to unlock insights</span>
              <ArrowRight size={14} color="#F87171" />
            </div>
          )}

          {/* Gap to Goal diagnostic — shows when behind pace */}
          {gapDiagnosis && gapDiagnosis.length > 0 && (
            <div style={{ marginTop:16, display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.08em" }}>
                Gap to Goal — Why You're Behind
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {gapDiagnosis.map((flag, fi) => {
                  const severityColor = flag.severity === "critical" ? "#F87171" : flag.severity === "high" ? "#FBBF24" : "#94A3B8";
                  const severityBg    = flag.severity === "critical" ? "rgba(248,113,113,0.12)" : flag.severity === "high" ? "rgba(251,191,36,0.10)" : "rgba(148,163,184,0.08)";
                  return (
                    <div key={fi} onClick={() => onNavigate(flag.nav)}
                      style={{ flex:"1 1 260px", padding:"12px 14px", background:severityBg,
                        border:`1px solid ${severityColor}30`, borderRadius:10, cursor:"pointer",
                        transition:"border-color 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = severityColor + "70"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = severityColor + "30"}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ fontSize:14 }}>{flag.icon}</span>
                          <span style={{ fontSize:11, fontWeight:800, color:severityColor, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                            {fi === 0 ? "Primary: " : "Also: "}{flag.label}
                          </span>
                        </div>
                        <span style={{ fontSize:9, fontWeight:700, color:severityColor,
                          background:`${severityColor}20`, borderRadius:4, padding:"2px 6px",
                          textTransform:"uppercase", letterSpacing:"0.05em", flexShrink:0, marginLeft:8 }}>
                          {flag.severity}
                        </span>
                      </div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", lineHeight:1.5, marginBottom:8 }}>{flag.detail}</div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>Owner: {flag.owner}</span>
                        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <span style={{ fontSize:11, color:severityColor, fontWeight:700 }}>{flag.action}</span>
                          <ArrowRight size={11} color={severityColor} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── POR SETUP COMPLETENESS (shown until fully done) ─────────────── */}
      {porPct < 1 && (
        <div style={{ marginBottom:24 }}>
          <SectionHeader title="POR Setup" sub={`${porComplete} of ${porSteps.length} steps complete`} action="Open Setup" onAction={() => onNavigate("planbuilder")} />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:8 }}>
            {porSteps.map((s, i) => <SetupStep key={i} done={s.done} label={s.label} nav={s.nav} onNavigate={onNavigate} />)}
          </div>
        </div>
      )}

      {/* ── ACTION QUEUE ─────────────────────────────────────────────────── */}
      {topActions.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <SectionHeader title="Action Queue" sub="What needs your attention right now" />
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {topActions.map((a, i) => (
              <div key={i} onClick={() => onNavigate(a.nav)}
                style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px",
                  background:"#FFF", border:`1px solid ${a.urgency==="high"?"rgba(220,38,38,0.20)":"rgba(0,0,0,0.07)"}`,
                  borderRadius:10, cursor:"pointer", transition:"box-shadow 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                <span style={{ fontSize:18 }}>{a.icon}</span>
                <span style={{ flex:1, fontSize:12, fontWeight:500, color:"#333" }}>{a.text}</span>
                <span style={{ fontSize:11, fontWeight:700, color:"#CC1A1A", whiteSpace:"nowrap" }}>{a.cta}</span>
                <ChevronRight size={13} color="#CC1A1A" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── POR SHORTFALL CARDS (when HubSpot connected) ─────────────────── */}
      {hasData && (planWR > 0 || planACV > 0 || planCoverage > 0) && (
        <div style={{ marginBottom:24 }}>
          <SectionHeader title="Plan vs Actuals" sub="Key metrics against your POR targets" action="POR Dashboard" onAction={() => onNavigate("dashboard")} />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
            {planWR > 0 && liveWR > 0 && (
              <GapCard label="Win Rate (L12M)" plan={planWR} actual={liveWR}
                planLabel={pct(planWR)} actualLabel={pct(liveWR)}
                suffix="%" ratio={liveWR / planWR}
                nav="coaching" navLabel="Rep Coaching" onNavigate={onNavigate} icon="🎯" />
            )}
            {planCoverage > 0 && (
              <GapCard label="Pipeline Coverage" plan={planCoverage} actual={coverage}
                planLabel={`${planCoverage}x`} actualLabel={`${coverage.toFixed(1)}x`}
                ratio={coverage / planCoverage}
                nav="coverage" navLabel="Account Coverage" onNavigate={onNavigate} icon="🪣" />
            )}
            {planACV > 0 && liveACV > 0 && (
              <GapCard label="Avg Deal Size" plan={planACV} actual={liveACV}
                planLabel={fmtM(planACV)} actualLabel={fmtM(liveACV)}
                ratio={liveACV / planACV}
                nav="coaching" navLabel="Rep Coaching" onNavigate={onNavigate} icon="💰" />
            )}
            {annualTarget > 0 && reps.length > 0 && (
              <TeamCoverageCard
                teamStats={teamStats}
                totalQuota={totalQuota}
                annualTarget={annualTarget}
                onNavigate={onNavigate} />
            )}
          </div>
        </div>
      )}

      {/* ── TEAM BREAKDOWN ───────────────────────────────────────────────── */}
      {teamStats.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <SectionHeader title="Team Performance" sub="Attainment vs plan quota" action="Rep Coaching" onAction={() => onNavigate("coaching")} />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:12 }}>
            {teamStats.map((ts, ti) => {
              const hasPerfTargets = ts.targets.winRate || ts.targets.avgDealSize;
              const teamWRRatio    = ts.targets.winRate && ts.teamWR != null ? ts.teamWR / (ts.targets.winRate / 100) : null;
              const attRatio       = ts.attainment;
              const coverageRatio  = ts.teamQuota > 0 && planCoverage > 0 ? (ts.teamPipe / ts.teamQuota) / planCoverage : null;
              return (
                <div key={ti} onClick={() => onNavigate("coaching")}
                  style={{ background:"#FFF", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", overflow:"hidden", cursor:"pointer" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 3px 16px rgba(0,0,0,0.10)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                  {/* Team header */}
                  <div style={{ padding:"10px 14px", borderBottom:`3px solid ${ts.color}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:800, color:"#111" }}>{ts.team}</div>
                      <div style={{ fontSize:10, color:"#888" }}>{ts.teamReps.length} rep{ts.teamReps.length !== 1 ? "s" : ""}</div>
                    </div>
                    {hasData && attRatio != null ? (
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:20, fontWeight:900, color: clr(attRatio), fontFamily:"'DM Mono',monospace" }}>{pct(attRatio)}</div>
                        <div style={{ fontSize:9, color:"#AAA", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>attainment</div>
                      </div>
                    ) : (
                      <div style={{ fontSize:11, color:"#CCC" }}>{fmtM(ts.teamQuota)}</div>
                    )}
                  </div>
                  {/* Progress bar */}
                  {hasData && attRatio != null && (
                    <div style={{ padding:"8px 14px 0" }}>
                      <div style={{ height:4, background:"#F0F0F0", borderRadius:99, overflow:"hidden" }}>
                        <div style={{ height:"100%", background: clr(attRatio), borderRadius:99, width:`${Math.min(100, attRatio * 100)}%`, transition:"width 0.5s" }} />
                      </div>
                    </div>
                  )}
                  {/* Metrics grid */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", padding:"10px 14px", gap:8 }}>
                    {[
                      { label:"Booked", val: hasData ? fmtM(ts.teamBooks) : "—", sub: fmtM(ts.teamQuota) + " quota", color: hasData && attRatio != null ? clr(attRatio) : "#888" },
                      { label:"Pipeline", val: hasData ? fmtM(ts.teamPipe) : "—", sub: coverageRatio != null ? `${(ts.teamPipe / ts.teamQuota).toFixed(1)}x cov` : "", color: coverageRatio != null ? clr(coverageRatio) : "#888" },
                      { label:"Win Rate", val: hasData && ts.teamWR != null ? pct(ts.teamWR) : "—", sub: ts.targets.winRate ? `${ts.targets.winRate}% plan · CYtD` : "CYtD · no target", color: teamWRRatio != null ? clr(teamWRRatio) : "#888" },
                    ].map((m, mi) => (
                      <div key={mi}>
                        <div style={{ fontSize:9, fontWeight:700, color:"#AAA", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:2 }}>{m.label}</div>
                        <div style={{ fontSize:14, fontWeight:800, color: m.color, fontFamily:"'DM Mono',monospace" }}>{m.val}</div>
                        {m.sub && <div style={{ fontSize:9, color:"#BBB" }}>{m.sub}</div>}
                      </div>
                    ))}
                  </div>
                  {!hasData && hasPerfTargets && (
                    <div style={{ padding:"0 14px 10px", fontSize:10, color:"#BBB" }}>
                      WR target: {ts.targets.winRate}% · ACV target: {ts.targets.avgDealSize ? fmtM(parseDollar(ts.targets.avgDealSize)) : "—"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── FORECAST BREAKDOWN ───────────────────────────────────────────── */}
      {hasData && forecastData && (
        <div style={{ marginBottom:24 }}>
          <SectionHeader title="Forecast" sub="Pipeline by category · weighted projection" action="POR Dashboard" onAction={() => onNavigate("dashboard")} />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
            {[
              { label:"Commit",          val:forecastData.commitVal,  count:forecastData.commit.length,    color:"#16A34A", bg:"rgba(22,163,74,0.06)",   tip:"High-confidence deals" },
              { label:"Best Case",       val:forecastData.bestVal,    count:forecastData.bestCase.length,  color:"#D97706", bg:"rgba(217,119,6,0.06)",   tip:"Likely but not certain" },
              { label:"Pipeline",        val:forecastData.pipeVal,    count:forecastData.pipeline.length,  color:"#7C3AED", bg:"rgba(124,58,237,0.06)", tip:"Earlier stage deals" },
              { label:"Weighted Total",  val:forecastData.weightedForecast, count:openDeals.length,        color:"#CC1A1A", bg:"rgba(204,26,26,0.06)",   tip:`Commit + 50% Best Case + ${pct(liveWR)} × Pipeline` },
            ].map((f,i) => (
              <div key={i} style={{ background:f.bg, border:`1px solid ${f.color}25`, borderRadius:12, padding:"16px 18px" }}>
                <div style={{ fontSize:10, fontWeight:700, color:f.color, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>{f.label}</div>
                <div style={{ fontSize:22, fontWeight:900, color:f.color, fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{fmtM(f.val)}</div>
                <div style={{ fontSize:11, color:"#888" }}>{f.count} deal{f.count!==1?"s":""} · {f.tip}</div>
                {annualTarget > 0 && <div style={{ fontSize:10, color:f.color, marginTop:4, fontWeight:700 }}>{pct(f.val/annualTarget)} of target</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── REP SCORECARD ────────────────────────────────────────────────── */}
      {repScorecard.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <SectionHeader title="Rep Scorecard" sub="Attainment · pipeline · win rate" action="Rep Coaching" onAction={() => onNavigate("coaching")} />
          <div style={{ background:"#FFF", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", overflow:"hidden" }}>
            {/* Header */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 90px 90px 90px 70px 80px", padding:"8px 16px", background:"#F8F8F8", borderBottom:"1px solid rgba(0,0,0,0.06)" }}>
              {["Rep","Attainment","Booked","Pipeline","# Deals","Win Rate"].map((h,i) => (
                <div key={i} style={{ fontSize:9, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.06em", textAlign:i>0?"center":"left" }}>{h}</div>
              ))}
            </div>
            {repScorecard.map((r, i) => {
              const RAG = { green:"#16A34A", yellow:"#D97706", red:"#DC2626", gray:"#aaa" };
              const c = RAG[r.status];
              return (
                <div key={i} onClick={() => onNavigate("coaching")}
                  style={{ display:"grid", gridTemplateColumns:"1fr 90px 90px 90px 70px 80px", padding:"10px 16px",
                    borderBottom:i<repScorecard.length-1?"1px solid rgba(0,0,0,0.04)":"none",
                    cursor:"pointer", background:"#FFF" }}
                  onMouseEnter={e => e.currentTarget.style.background="rgba(0,0,0,0.015)"}
                  onMouseLeave={e => e.currentTarget.style.background="#FFF"}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:c, flexShrink:0 }}/>
                    <span style={{ fontSize:12, fontWeight:600, color:"#111" }}>{r.rep.name}</span>
                    {r.rep.team && <span style={{ fontSize:9, color:"#aaa", background:"rgba(0,0,0,0.05)", borderRadius:4, padding:"1px 6px" }}>{r.rep.team}</span>}
                  </div>
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontSize:13, fontWeight:800, color:c, fontFamily:"'DM Mono',monospace" }}>{r.att!=null?pct(r.att):"—"}</div>
                    {r.quota>0 && <div style={{ fontSize:9, color:"#aaa" }}>{fmtM(r.quota)} quota</div>}
                  </div>
                  <div style={{ textAlign:"center", fontSize:12, fontWeight:700, color:"#111", fontFamily:"'DM Mono',monospace" }}>{r.booked>0?fmtM(r.booked):"—"}</div>
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"#7C3AED", fontFamily:"'DM Mono',monospace" }}>{r.pipe>0?fmtM(r.pipe):"—"}</div>
                    {r.cov!=null && <div style={{ fontSize:9, color:r.cov>=1?"#16A34A":r.cov>=0.7?"#D97706":"#DC2626" }}>{(r.cov*planCoverage).toFixed(1)}x cov</div>}
                  </div>
                  <div style={{ textAlign:"center", fontSize:12, color:"#555" }}>{r.openCount}</div>
                  <div style={{ textAlign:"center", fontSize:12, fontWeight:600, color:"#555" }}>{r.wr!=null?pct(r.wr):"—"}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MONTH-OVER-MONTH ─────────────────────────────────────────────── */}
      {momData && (
        <div style={{ marginBottom:24 }}>
          <SectionHeader title="Period Comparison" sub="This month vs last month vs target" />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
            {[
              { label:`This Month`,    val:momData.thisMonthVal, tgt:momData.thisMonthTgt, att:momData.thisAtt, color:"#CC1A1A" },
              { label:`Last Month`,    val:momData.lastMonthVal, tgt:momData.lastMonthTgt, att:momData.lastAtt, color:"#555" },
              { label:"Month-on-Month",val:null, mom:momData.mom, color: momData.mom==null?"#555":momData.mom>=0?"#16A34A":"#DC2626" },
            ].map((m,i) => (
              <div key={i} style={{ background:"#FFF", border:"1px solid rgba(0,0,0,0.07)", borderRadius:12, padding:"16px 18px" }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>{m.label}</div>
                {m.mom !== undefined && m.val === null ? (
                  <div>
                    <div style={{ fontSize:28, fontWeight:900, color:m.color, fontFamily:"'DM Mono',monospace" }}>
                      {m.mom==null?"—":`${m.mom>=0?"+":""}${pct(m.mom)}`}
                    </div>
                    <div style={{ fontSize:11, color:"#888", marginTop:4 }}>
                      {m.mom==null?"No prior month data":m.mom>=0?"Above last month":"Below last month"}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize:24, fontWeight:900, color:m.att!=null?clr(m.att):"#111", fontFamily:"'DM Mono',monospace" }}>{fmtM(m.val)}</div>
                    <div style={{ fontSize:11, color:"#888", marginTop:4 }}>
                      {m.att!=null ? `${pct(m.att)} of ${fmtM(m.tgt)} target` : `Target: ${fmtM(m.tgt)}`}
                    </div>
                    {m.att!=null && (
                      <div style={{ marginTop:8, height:4, background:"#F0F0F0", borderRadius:99, overflow:"hidden" }}>
                        <div style={{ height:"100%", background:clr(m.att), borderRadius:99, width:`${Math.min(100,m.att*100)}%` }}/>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── RISK FLAGS + CLOSING THIS WEEK ───────────────────────────────── */}
      {hasData && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:24 }}>

          {/* Risk flags */}
          <div>
            <SectionHeader title="Pipeline Risks" sub="Concentration, activity, and forecast quality" action="CRM Health" onAction={() => onNavigate("crm")} />
            <div style={{ background:"#FFF", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", overflow:"hidden" }}>
              {pipelineRisks.length === 0 ? (
                <div style={{ padding:"20px 16px", textAlign:"center", fontSize:12, color:"#16A34A", fontWeight:600 }}>✅ No pipeline risks detected</div>
              ) : (
                pipelineRisks.map((r, ri) => {
                  const sevBg = r.severity==="high" ? `${r.color}08` : "transparent";
                  return (
                    <div key={ri} onClick={() => onNavigate("crm")}
                      style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"11px 16px",
                        borderBottom: ri<pipelineRisks.length-1?"1px solid rgba(0,0,0,0.04)":"none",
                        cursor:"pointer", background:sevBg }}
                      onMouseEnter={e => e.currentTarget.style.background="rgba(0,0,0,0.02)"}
                      onMouseLeave={e => e.currentTarget.style.background=sevBg}>
                      <span style={{ fontSize:16, marginTop:1, flexShrink:0 }}>{r.icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                          <span style={{ fontSize:11, fontWeight:700, color:r.color }}>{r.label}</span>
                          <span style={{ fontSize:8, fontWeight:700, color:r.color, background:`${r.color}15`,
                            borderRadius:4, padding:"1px 5px", textTransform:"uppercase", letterSpacing:"0.05em" }}>
                            {r.severity}
                          </span>
                        </div>
                        <div style={{ fontSize:11, color:"#555", lineHeight:1.5 }}>{r.detail}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Closing this week */}
          <div>
            <SectionHeader title="Closing This Week"
              sub={`${wk.toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${wkEnd.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`} />
            <div style={{ background:"#FFF", borderRadius:12, border:`1px solid ${closing.length > 0 ? "rgba(22,163,74,0.2)" : "rgba(0,0,0,0.07)"}`, overflow:"hidden" }}>
              {closing.length === 0 ? (
                <div style={{ padding:"20px 16px", textAlign:"center", fontSize:12, color:"#AAA" }}>No deals closing this week</div>
              ) : (
                <>
                  <div style={{ padding:"10px 16px", background:"rgba(22,163,74,0.06)", borderBottom:"1px solid rgba(22,163,74,0.10)", display:"flex", justifyContent:"space-between" }}>
                    <span style={{ fontSize:12, fontWeight:700, color:"#15803d" }}>🎯 {closing.length} deal{closing.length !== 1 ? "s" : ""}</span>
                    <span style={{ fontSize:12, fontWeight:800, color:"#15803d", fontFamily:"'DM Mono',monospace" }}>{fmtM(closing.reduce((a,d)=>a+amt(d),0))}</span>
                  </div>
                  {closing.slice(0, 4).map((d, i) => {
                    const name = d.properties?.[F.dealname] || "Deal";
                    const rep  = reps.find(r => String(r.hubspotOwnerId) === ownId(d));
                    return (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 16px", borderBottom: i < Math.min(closing.length, 4) - 1 ? "1px solid rgba(0,0,0,0.04)" : "none" }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:"#111", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</div>
                          <div style={{ fontSize:10, color:"#888" }}>{rep?.name || "Unassigned"} · {fmtDate(closeD(d))}</div>
                        </div>
                        <div style={{ fontSize:12, fontWeight:700, color:"#15803d", fontFamily:"'DM Mono',monospace", flexShrink:0 }}>{fmtM(amt(d))}</div>
                      </div>
                    );
                  })}
                  {closing.length > 4 && <div style={{ padding:"8px 16px", fontSize:11, color:"#888" }}>+{closing.length - 4} more deals</div>}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── QUICK NAVIGATION (always visible) ────────────────────────────── */}
      <div>
        <SectionHeader title="Navigate" sub="Jump to any module" />
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
          {[
            { nav:"dashboard",   icon:"📊", label:"POR Dashboard",    sub:"Plan vs actuals live" },
            { nav:"coaching",    icon:"🏆", label:"Rep Coaching",     sub:"Attainment · pipeline · flags" },
            { nav:"crm",         icon:"🔍", label:"CRM Health",       sub:"Data quality · risk alerts" },
            { nav:"coverage",    icon:"🗺️", label:"Account Coverage", sub:"Territory · dark accounts" },
            { nav:"por",         icon:"🎯", label:"POR Builder",      sub:"Revenue scenarios" },
            { nav:"planbuilder", icon:"📋", label:"Setup",            sub:"Team · targets · matrix" },
            { nav:"planbuilder", icon:"⚙️", label:"Admin & Setup",     sub:"HubSpot · team · targets" },
          ].map((n, i) => (
            <div key={i} onClick={() => onNavigate(n.nav)}
              style={{ background:"#FFF", borderRadius:10, border:"1px solid rgba(0,0,0,0.07)", padding:"12px 14px", cursor:"pointer" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(204,26,26,0.25)"; e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.07)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.07)"; e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ fontSize:20, marginBottom:6 }}>{n.icon}</div>
              <div style={{ fontSize:12, fontWeight:700, color:"#111" }}>{n.label}</div>
              <div style={{ fontSize:10, color:"#AAA", marginTop:2 }}>{n.sub}</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default MorningHome;
